import * as React from 'react';
import { Box } from '@material-ui/core';
import { Form, Formik, FormikHelpers } from 'formik';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router';
import toast from 'react-hot-toast';

import { FormikTextField } from '@components/fieldBindings/FormikTextField';
import { FormikSubmitButton } from '@components/fieldBindings/FormikSubmitButton';
import { RouteNames } from '@constants/RouteNames';
import client from '@api/client';
import i18n from '@src/i18n';
import { CenterColumnPage } from '../../Layouts/CenterColumnPage/CenterColumnPage';

type SetPasswordFormValues = {
  password: string;
  confirmPassword: string;
};

const validationSchema = Yup.object().shape({
  password: Yup.string().min(8, i18n.t('common.required')).required(i18n.t('common.required')),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref('password')], i18n.t('pages.setPassword.passwordMismatch'))
    .required(i18n.t('common.required')),
});

export const SetPassword: React.FC = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const code = new URLSearchParams(location.search).get('code');

  const handleSubmit = async ({ password }: SetPasswordFormValues, util: FormikHelpers<SetPasswordFormValues>) => {
    if (!code) {
      toast.error(t('pages.setPassword.missingCodeMsg') as string);
      return;
    }
    try {
      await client.setPassword(code, password);
      history.push(RouteNames.ROOT);
    } catch (err) {
      toast.error(t('pages.setPassword.errorMsg') as string);
      util.setSubmitting(false);
    }
  };

  return (
    <CenterColumnPage pageTitle={t('pages.setPassword.titleText') as string}>
      <Formik<SetPasswordFormValues>
        onSubmit={handleSubmit}
        initialValues={{ password: '', confirmPassword: '' }}
        validateOnBlur={false}
        validationSchema={validationSchema}
      >
        {({ isValid, dirty }) => (
          <Form>
            <Box display="flex" flexDirection="column" mb={2}>
              <FormikTextField
                id="password"
                name="password"
                type="password"
                placeholder={t('pages.setPassword.password.placeholder')}
                margin="normal"
                autoComplete="new-password"
                autoFocus
              />
              <FormikTextField
                id="confirmPassword"
                name="confirmPassword"
                type="password"
                placeholder={t('pages.setPassword.confirmPassword.placeholder')}
                margin="normal"
                autoComplete="new-password"
              />
            </Box>
            <FormikSubmitButton disabled={!(isValid && dirty)}>
              {t('pages.setPassword.submitButtonText')}
            </FormikSubmitButton>
          </Form>
        )}
      </Formik>
    </CenterColumnPage>
  );
};

export default SetPassword;
